import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Store, select } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { GridService } from '@wilson/ag-grid/service';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterModule } from '@wilson/filter';
import { InputPortalComponent } from '@wilson/forms';
import {
  FetchGeneralCompanySettingsIfNeeded,
  GeneralCompanySettingsState,
} from '@wilson/general-company-settings';
import { UserVacationV2 } from '@wilson/absences';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { LocalizationState } from '@wilson/localization';
import { numberFormatter } from '@wilson/utils';
import { NzInputModule } from 'ng-zorro-antd/input';

@Component({
  selector: 'wilson-vacation-management-drawer',
  imports: [
    NzButtonModule,
    NzDividerModule,
    NzStatisticModule,
    TranslateModule,
    FeatureFlagsModule,
    FilterModule,
    FormsModule,
    InputPortalComponent,
    NzInputModule,
    NzDrawerModule,
    NzIconModule,
    NzTooltipModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './vacation-management-drawer.component.html',
  styleUrl: './vacation-management-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GridService],
})
export class VacationManagementDrawerComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  #store = inject(Store);
  readonly #lang = select(LocalizationState.getLang);

  @Input() visible = false;
  @Input() selectedUserVacations: UserVacationV2[] = [];
  @Input() selectedYear: number | null = null;
  @Input() showMask = false;
  @Output() closeDrawer = new EventEmitter<boolean>(false);
  @Output() saveDrawer = new EventEmitter<UserVacationV2[]>();

  form: FormGroup = this.fb.group({
    leaveDays: [0],
    specialLeaveDays: [0],
    carryoverDays: [0],
  });

  readonly previousYearCarryoverDays = signal<number | null>(0);
  readonly computedLocalizedCarryOverDays = computed(() => {
    return numberFormatter(this.#lang())(this.previousYearCarryoverDays() ?? 0);
  });
  readonly hasMixed = signal(false);
  readonly #selectedYearSignal = signal<number | null>(null);
  #isLeaveCarryoverEnabled = select(
    GeneralCompanySettingsState.isLeaveCarryoverEnabled,
  );

  readonly shouldIncludeCarryover = computed(() => {
    const year = this.#selectedYearSignal();
    return (
      UserVacationV2.isCarryoverApplicable(year) &&
      this.#isLeaveCarryoverEnabled()
    );
  });

  readonly #takenVacationDays = signal<number>(0);
  readonly #takenSpecialVacationDays = signal<number>(0);
  readonly #formValues = toSignal(this.form.valueChanges, {
    initialValue: {
      leaveDays: 0 as number | null,
      specialLeaveDays: 0 as number | null,
      carryoverDays: 0 as number | null,
    },
  });

  readonly computedUsedDays = computed(() => {
    return this.#takenVacationDays() + this.#takenSpecialVacationDays();
  });

  readonly computedFormattedUsedDays = computed(() => {
    return numberFormatter(this.#lang())(this.computedUsedDays());
  });

  readonly computedBalance = computed(() => {
    const {
      leaveDays = 0,
      specialLeaveDays = 0,
      carryoverDays = 0,
    } = this.#formValues();

    const tempVacation = new UserVacationV2({
      availableVacationDays: leaveDays,
      availableSpecialVacationDays: specialLeaveDays,
      carryover: carryoverDays,
      previousYearCarryover: this.previousYearCarryoverDays(),
      takenVacationDays: this.#takenVacationDays(),
      takenSpecialVacationDays: this.#takenSpecialVacationDays(),
    });

    const available = UserVacationV2.calculateTotalAvailable(
      tempVacation,
      this.#selectedYearSignal(),
    );

    const formattedAvailable = numberFormatter(this.#lang())(
      available - this.computedUsedDays(),
    );
    return formattedAvailable;
  });

  ngOnInit(): void {
    this.#store.dispatch(new FetchGeneralCompanySettingsIfNeeded());
  }

  ngOnChanges(): void {
    this.#selectedYearSignal.set(this.selectedYear);

    if (this.selectedUserVacations.length === 0) return;

    if (this.selectedUserVacations.length === 1) {
      this.patchFormWithSingleVacation();
    } else {
      this.patchFormWithMultipleVacations();
    }
  }

  private patchFormWithSingleVacation(): void {
    const vacation = this.selectedUserVacations[0];
    this.form.patchValue({
      leaveDays: vacation.availableVacationDays,
      specialLeaveDays: vacation.availableSpecialVacationDays,
      carryoverDays: vacation.carryover,
    });
    this.hasMixed.set(false);
    this.previousYearCarryoverDays.set(vacation.previousYearCarryover ?? null);
    this.#takenVacationDays.set(vacation.takenVacationDays ?? 0);
    this.#takenSpecialVacationDays.set(vacation.takenSpecialVacationDays ?? 0);
  }

  private patchFormWithMultipleVacations(): void {
    const { availableVacationDays, availableSpecialVacationDays, carryover } =
      UserVacationV2.checkUniformity(this.selectedUserVacations);

    const anyFieldMixed = [
      availableVacationDays,
      availableSpecialVacationDays,
      carryover,
    ].includes('mixed');

    this.form.patchValue({
      leaveDays:
        availableVacationDays !== 'mixed' ? availableVacationDays : null,
      specialLeaveDays:
        availableSpecialVacationDays !== 'mixed'
          ? availableSpecialVacationDays
          : null,
      carryoverDays: carryover !== 'mixed' ? carryover : null,
    });

    this.hasMixed.set(anyFieldMixed);
    this.previousYearCarryoverDays.set(null);
    this.#takenVacationDays.set(0);
    this.#takenSpecialVacationDays.set(0);
  }

  saveVacations(): void {
    if (!this.form.valid) {
      return;
    }

    if (this.selectedUserVacations.length > 0) {
      const updatedVacations = this.selectedUserVacations.map((vacation) => ({
        ...vacation,
        availableVacationDays:
          this.form.value.leaveDays !== null
            ? this.form.value.leaveDays
            : vacation.availableVacationDays,
        availableSpecialVacationDays:
          this.form.value.specialLeaveDays !== null
            ? this.form.value.specialLeaveDays
            : vacation.availableSpecialVacationDays,
        carryover:
          this.form.value.carryoverDays !== null
            ? this.form.value.carryoverDays
            : vacation.carryover,
      }));

      this.saveDrawer.emit(updatedVacations);
    }
  }

  onDrawerClose(): void {
    this.closeDrawer.emit(false);
  }
}
